import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconArrowRightOutline } from '@cae/cobalt-react/icons/outline/CobaltIconArrowRightOutline';
import { CobaltIconExternalLinkOutline } from '@cae/cobalt-react/icons/outline/CobaltIconExternalLinkOutline';
import { getFullName } from '@shared/helpers/get-full-name/getFullName';
import { COMMON_TICKET_URL } from '@shared/utils/support-urls/supportUrls';
import * as React from 'react';

import type { EmailDiscrepancyContentProps } from './EmailDiscrepancyContent.types';

import { DiscrepancyIllustration } from '../illustrations/DiscrepancyIllustration';
import sharedStyles from '../shared/DiscrepancyLayout.module.css';

const EmailDiscrepancyContent = React.memo(
  ({
    sfContact,
    caeConnectContact,
    onGoHome,
  }: Readonly<EmailDiscrepancyContentProps>): React.ReactElement => {
    const sfName = getFullName(
      sfContact.firstName,
      sfContact.middleName,
      sfContact.lastName
    );
    const connectName = getFullName(
      caeConnectContact.firstName,
      caeConnectContact.middleName,
      caeConnectContact.lastName
    );

    return (
      <div
        className={sharedStyles.discrepancyPage}
        data-testid="email-discrepancy-content"
      >
        <DiscrepancyIllustration />

        <section
          className={sharedStyles.contentSection}
          aria-labelledby="email-discrepancy-title"
        >
          <h1 className="heading-02" id="email-discrepancy-title">
            It looks like the contact you selected already exists in CAE
            Connect, but there are discrepancies in the information.
          </h1>

          <div className={sharedStyles.comparisonContainer}>
            <CobaltTile className={sharedStyles.comparisonBox}>
              <p className={`body-01 ${sharedStyles.comparisonLabel}`}>
                Salesforce data
              </p>
              <p className="body-02">{sfName}</p>
              <p className="body-02">{sfContact.email}</p>
            </CobaltTile>

            <span className={sharedStyles.icon}>
              <CobaltIconArrowRightOutline size="medium" />
            </span>

            <CobaltTile className={sharedStyles.comparisonBox}>
              <p className={`body-01 ${sharedStyles.comparisonLabel}`}>
                CAE Connect data
              </p>
              <p className="body-02">{connectName}</p>
              <p className="body-02">{caeConnectContact.email}</p>
            </CobaltTile>
          </div>

          <div className={sharedStyles.contentFooter}>
            <p className="body-02">
              To resolve this, you can update the Salesforce contact information
              to match the information used in CAE Connect.
            </p>
            <p className="body-02">
              If you prefer to update the CAE Connect information instead,
              please open an IT ticket requesting that the information for{' '}
              {sfName} ({sfContact.sfMasterContactId}) be changed in CAE
              Connect.
            </p>
          </div>
        </section>

        <div className={sharedStyles.actions}>
          <CobaltLink href={COMMON_TICKET_URL} target="_blank">
            <CobaltButton type="button" variant="primary">
              Open a ticket
              <CobaltIconExternalLinkOutline />
            </CobaltButton>
          </CobaltLink>
          <CobaltButton type="button" variant="ghost" onClick={onGoHome}>
            Go to Homepage
          </CobaltButton>
        </div>
      </div>
    );
  }
);

EmailDiscrepancyContent.displayName = 'EmailDiscrepancyContent';

export { EmailDiscrepancyContent };
